// Sunucu dışı yedek paneli (madde 15) — uygulamadan bağımsız. Solda R2
// ayarları (Account ID, bucket, anahtarlar, şifreleme parolası, saklama
// süresi, son gönderim), sağda R2'deki yedekler + "Sunucuya İndir".
// Backend uçları (varsayılan `/backups/offsite`): GET (ayarlar + liste),
// PUT (kaydet), POST /test, POST /send-latest, POST /fetch { key }.
// HTTP istemcisi, bildirim/onay pencereleri ve metinler uygulamadan verilir.

import { FormEvent, useEffect, useState } from "react";
import "./offsiteBackup.css";

export interface OffsiteSettings {
  enabled: boolean;
  r2AccountId: string;
  r2Bucket: string;
  r2AccessKeyId: string;
  r2SecretAccessKeySet: boolean;
  passphraseSet: boolean;
  keepDays: number;
  lastAt: string | null;
  lastStatus: string | null;
}

export interface OffsiteObject {
  key: string;
  size: number;
  lastModified: string;
}

/** axios ya da benzeri: `{ data }` dönen get/post/put. */
export interface OffsiteHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  put<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

export interface OffsiteStrings {
  settingsTitle: string;
  listTitle: string;
  hint: string;
  enabled: string;
  accountId: string;
  bucket: string;
  accessKeyId: string;
  secret: string;
  passphrase: string;
  secretKeep: string;
  passphraseHint: string;
  keepDays: string;
  lastStatus: string;
  save: string;
  test: string;
  sendNow: string;
  working: string;
  saved: string;
  savedWithPassphrase: string;
  failed: string;
  colFile: string;
  colDate: string;
  colSize: string;
  fetch: string;
  fetchConfirm: string;
  emptyList: string;
  show: string;
  hide: string;
}

export const DEFAULT_OFFSITE_STRINGS: OffsiteStrings = {
  settingsTitle: "R2 Ayarları",
  listTitle: "R2'deki Yedekler",
  hint: "Her gece alınan yedek, AES-256 ile şifrelenip Cloudflare R2'ye de gönderilir. Sunucu kaybolsa, disk bozulsa ya da fidye yazılımı bulaşsa bile veriler geri getirilebilir.",
  enabled: "Her gece sunucu dışına gönder",
  accountId: "Cloudflare Account ID",
  bucket: "R2 Bucket adı",
  accessKeyId: "Access Key ID",
  secret: "Secret Access Key",
  passphrase: "Şifreleme parolası",
  secretKeep: "Kayıtlı — değiştirmek için yeni değer girin",
  passphraseHint:
    "⚠️ Bu parolayı sunucu DIŞINDA (şifre yöneticisi vb.) mutlaka saklayın. Sunucu kaybolursa yedekleri yalnızca bu parolayla açabilirsiniz. En az 12 karakter.",
  keepDays: "R2'de saklama süresi (gün)",
  lastStatus: "Son gönderim",
  save: "Kaydet",
  test: "Bağlantıyı Test Et",
  sendNow: "Son Yedeği Şimdi Gönder",
  working: "Çalışıyor…",
  saved: "Ayarlar kaydedildi.",
  savedWithPassphrase: "Ayarlar kaydedildi. Şifreleme parolasını sunucu dışında sakladığınızdan emin olun.",
  failed: "İşlem başarısız oldu.",
  colFile: "Dosya",
  colDate: "Tarih",
  colSize: "Boyut",
  fetch: "Sunucuya İndir",
  fetchConfirm: "Bu yedek R2'den indirilip çözülecek ve yerel yedek listesine eklenecek (henüz geri yüklenmez). Devam edilsin mi?",
  emptyList: "R2'de henüz yedek yok.",
  show: "Göster",
  hide: "Gizle",
};

export interface OffsiteBackupPanelProps {
  http: OffsiteHttp;
  basePath?: string;
  notify: (message: string, variant: "info" | "danger") => void | Promise<void>;
  confirm: (message: string) => Promise<boolean>;
  /** Yedek sunucuya indirildikten sonra (yerel listeyi yenilemek için). */
  onFetched?: () => void;
  /** Sunucu hata mesajını çıkar (ör. axios `response.data.message`). */
  errorMessage?: (err: unknown) => string | undefined;
  strings?: OffsiteStrings;
  buttons?: { primary?: string; secondary?: string; row?: string };
  formatDate?: (iso: string) => string;
}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

function SecretInput(props: { value: string; onChange: (v: string) => void; placeholder: string; autoComplete: string; show: string; hide: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="ofs-secret">
      <input
        type={visible ? "text" : "password"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete}
      />
      <button type="button" onClick={() => setVisible((v) => !v)} aria-label={visible ? props.hide : props.show} title={visible ? props.hide : props.show}>
        {visible ? "🙈" : "👁"}
      </button>
    </div>
  );
}

export function OffsiteBackupPanel({
  http,
  basePath = "/backups/offsite",
  notify,
  confirm,
  onFetched,
  errorMessage,
  strings = DEFAULT_OFFSITE_STRINGS,
  buttons,
  formatDate = (iso) => new Date(iso).toLocaleString(),
}: OffsiteBackupPanelProps) {
  const s = strings;
  const btn = { primary: "btn-primary", secondary: "btn-secondary", row: "btn-download", ...buttons };
  const [settings, setSettings] = useState<OffsiteSettings | null>(null);
  const [objects, setObjects] = useState<OffsiteObject[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [form, setForm] = useState({ enabled: false, r2AccountId: "", r2Bucket: "", r2AccessKeyId: "", keepDays: 30 });
  const [secret, setSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = () =>
    http.get<{ settings: OffsiteSettings; objects: OffsiteObject[]; listError: string | null }>(basePath).then((res) => {
      const st = res.data.settings;
      setSettings(st);
      setObjects(res.data.objects);
      setListError(res.data.listError);
      setForm({ enabled: st.enabled, r2AccountId: st.r2AccountId, r2Bucket: st.r2Bucket, r2AccessKeyId: st.r2AccessKeyId, keepDays: st.keepDays });
    });

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath]);

  const run = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    try {
      await fn();
    } catch (err) {
      await notify(errorMessage?.(err) ?? s.failed, "danger");
    } finally {
      setBusy(null);
    }
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    const withPassphrase = !!passphrase;
    void run("save", async () => {
      await http.put(basePath, {
        ...form,
        ...(secret ? { r2SecretAccessKey: secret } : {}),
        ...(passphrase ? { passphrase } : {}),
      });
      setSecret("");
      setPassphrase("");
      await load();
      await notify(withPassphrase ? s.savedWithPassphrase : s.saved, "info");
    });
  };

  const post = (name: string, path: string, body?: unknown) =>
    run(name, async () => {
      const { data } = await http.post<{ message: string }>(`${basePath}${path}`, body);
      await load();
      if (name === "fetch") onFetched?.();
      await notify(data.message, "info");
    });

  if (!settings) return null;

  return (
    <div className="ofs-grid">
      <form className="ofs-card" onSubmit={save}>
        <h3>{s.settingsTitle}</h3>
        <p className="ofs-hint">{s.hint}</p>
        <label className="ofs-check">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          {s.enabled}
        </label>
        <label className="ofs-field">
          {s.accountId}
          <input type="text" value={form.r2AccountId} onChange={(e) => setForm({ ...form, r2AccountId: e.target.value.trim() })} />
        </label>
        <div className="ofs-row2">
          <label className="ofs-field">
            {s.bucket}
            <input type="text" value={form.r2Bucket} onChange={(e) => setForm({ ...form, r2Bucket: e.target.value.trim() })} />
          </label>
          <label className="ofs-field">
            {s.keepDays}
            <input
              type="number"
              min={1}
              max={3650}
              value={form.keepDays}
              onChange={(e) => setForm({ ...form, keepDays: Number(e.target.value) || 30 })}
            />
          </label>
        </div>
        <label className="ofs-field">
          {s.accessKeyId}
          <input type="text" value={form.r2AccessKeyId} onChange={(e) => setForm({ ...form, r2AccessKeyId: e.target.value.trim() })} />
        </label>
        <label className="ofs-field">
          {s.secret}
          <SecretInput
            value={secret}
            onChange={setSecret}
            placeholder={settings.r2SecretAccessKeySet ? s.secretKeep : ""}
            autoComplete="off"
            show={s.show}
            hide={s.hide}
          />
        </label>
        <label className="ofs-field">
          {s.passphrase}
          <SecretInput
            value={passphrase}
            onChange={setPassphrase}
            placeholder={settings.passphraseSet ? s.secretKeep : ""}
            autoComplete="new-password"
            show={s.show}
            hide={s.hide}
          />
        </label>
        <p className="ofs-hint">{s.passphraseHint}</p>
        <div className="ofs-actions">
          <button type="submit" className={btn.primary} disabled={!!busy}>
            {s.save}
          </button>
          <button type="button" className={btn.secondary} disabled={!!busy} onClick={() => void post("test", "/test")}>
            {busy === "test" ? s.working : s.test}
          </button>
        </div>
      </form>

      <div className="ofs-card">
        <h3>{s.listTitle}</h3>
        {settings.lastStatus && (
          <p className={settings.lastStatus.startsWith("HATA") ? "ofs-error" : "ofs-hint"}>
            {s.lastStatus}: {settings.lastAt ? formatDate(settings.lastAt) : ""} — {settings.lastStatus}
          </p>
        )}
        <div className="ofs-actions">
          <button type="button" className={btn.secondary} disabled={!!busy} onClick={() => void post("send", "/send-latest")}>
            {busy === "send" ? s.working : s.sendNow}
          </button>
        </div>
        {listError && <p className="ofs-error">{listError}</p>}
        {objects.length === 0 ? (
          !listError && <p className="ofs-hint">{s.emptyList}</p>
        ) : (
          <div className="ofs-table-wrap">
            <table className="ofs-table">
              <thead>
                <tr>
                  <th>{s.colFile}</th>
                  <th>{s.colDate}</th>
                  <th>{s.colSize}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {objects.map((o) => (
                  <tr key={o.key}>
                    <td className="ofs-file">{o.key.replace(/^[^/]+\//, "")}</td>
                    <td>{formatDate(o.lastModified)}</td>
                    <td>{formatSize(o.size)}</td>
                    <td>
                      <button
                        type="button"
                        className={btn.row}
                        disabled={!!busy}
                        onClick={async () => {
                          if (await confirm(s.fetchConfirm)) void post("fetch", "/fetch", { key: o.key });
                        }}
                      >
                        {busy === "fetch" ? s.working : s.fetch}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
